import React, { useEffect } from "react";
import InputText from "../InputText";
import LicensingTerm from "../Content/LicensingTerm";
import LicensingRegion from "../Content/LicensingRegion";
import { useFormik } from "formik";
import TypesOfRights from "../Content/Rights";
import Selector from "../Selector";
import { Box, Paper, Stack, Typography } from "@mui/material";
import UploadReceipt from "./UploadImage";
import { useState } from "react";
import * as yup from "yup";
import { createAgreement, updateAgreement } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  handleComplete,
  handleNext,
} from "../../redux/slices/addNewContentStepsSlice";
import { setAgreementDetails } from "../../redux/slices/agreementFormSlice";
import { uploadData } from "aws-amplify/storage";
import ReadOnlyInput from "../ReadOnlyInput";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import AgreementFileUpload from "./AgreementFileUpload";
import { EXCHANGE_RATE_URL } from "../../constants";
import axios from "axios";

//Validation schema for agreement form
const validationSchema = yup.object().shape({
  daysOfAgreement: yup.number().required("Term of Agreement is required"),
  dateOfAgreement: yup.string().required("Date Of Agreement is required!"),
  nameOfSupplier: yup.string().required("Name of Supplier is required!"),
  licensingCountry: yup
    .array()
    .of(yup.string())
    .min(1, "At least one country must be selected")
    .required("Licensing country is required"),

  licensingRegion: yup.string().required("Target Region is required!"),

  carrierType: yup
    .array()
    .of(yup.string())
    .min(1, "Please select at least a carrier type")
    .required("Types of rights are required"),

  airline: yup
    .array()
    .of(yup.string())
    .min(1, "Please select at least an airline")
    .required("Types of rights are required"),

  numberOfRefreshCycles: yup
    .number()
    .typeError("Number of refresh cycles must be a number")
    .positive("Number of refresh cycles must be a positive number")
    .required("Number of refresh cycles is required"),

  numberOfMonthsPerCycle: yup
    .number()
    .typeError("Number of months per cycle must be a number")
    .positive("Number of months per cycle must be a positive number")
    .integer("Number of months per cycle must be a whole number") // Ensure the value is an integer
    .required("Number of months per cycle is required"),

  paymentStatus: yup.string().required("Payment status is required"),
  currency: yup.string().required("Currency is required!"),

  contractValue: yup
    .number()
    .typeError("Contract value must be a number")
    .positive("Contract value must be a positive number")
    .required("Contract value is required"),

  //   paidReceipt: yup.string().required("Payment receipt is required"), // No specific validation for paid receipt
  amountPaid: yup
    .number()
    .test("is-required", "Amount of payment is required!", function (value) {
      const { paymentStatus } = this.parent;
      return (
        (paymentStatus !== "Paid" && paymentStatus !== "PartiallyPaid") ||
        !!value
      );
    })
    .typeError("Amount paid must be a number")
    .positive("Amount paid must be a positive number"),

  amountRemaining: yup
    .number()
    .nullable()
    .test("is-required", "Amount of payment is required!", function (value) {
      const { paymentStatus } = this.parent;
      // Allow null or empty unless paymentStatus is "PartiallyPaid"
      return paymentStatus !== "PartiallyPaid" || value !== null;
    })
    .typeError("Amount remaining must be a number or null")
    .test(
      "is-positive",
      "Amount remaining must be a positive number",
      function (value) {
        // Allow null, undefined or positive values
        return value === null || value === undefined || value > 0;
      }
    ),

  paymentPlan: yup.string().required("Payment plan is required"),
  paymentDueDate: yup.string().required("Payment Due Date is required!"),
  paymentTerm: yup.string().required("Payment term is required"),
  agreementFilePath: yup.string().required("Agreement document is required"),
  paymentReceiptFilePath: yup
    .string()
    .test("is-required", "Payment receipt is required!", function (value) {
      const { paymentStatus } = this.parent;
      return (
        (paymentStatus !== "Paid" && paymentStatus !== "PartiallyPaid") ||
        !!value
      );
    }),
});

const AgreementForm = () => {
  const params = useParams();
  const { agreementId } = params;
  const location = useLocation();
  const isEditRoute = location.pathname.includes("/edit");

  const agreementForm = useSelector((state) => state.agreementForm);

  const [isLoading, setIsLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [agreementFile, setAgreementFile] = useState(null);

  const dispatch = useDispatch();
  const user = useSelector((state) => state?.auth?.user);

  const client = generateClient();

  const formik = useFormik({
    initialValues: agreementForm,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log(isEditRoute, agreementId, "outer");
      if (isEditRoute && agreementId) {
        console.log(isEditRoute, agreementId, "inner");

        await handleUpdateAgreement(values);
        return;
      }

      if (agreementForm?.id) {
        dispatch(handleNext());
        return;
      } else {
        await handleCreateAgreement(values);
      }
    },
  });

  const handleCreateAgreement = async (values) => {
    try {
      setIsLoading(true);

      // Check if there's a new image file to upload
      if (imageFile instanceof File) {
        await uploadData({
          key: values.paymentReceiptFilePath,
          data: imageFile,
        }).result;
      }

      // Check if there's a new agreement file to upload
      if (agreementFile instanceof File) {
        await uploadData({
          key: values.agreementFilePath,
          data: agreementFile,
        }).result;
      } else if (!values.agreementFilePath) {
        // If no new file is selected and no existing file path, throw an error
        throw new Error("Agreement file is required");
      }

      const response = await client.graphql({
        query: createAgreement,
        variables: {
          input: {
            ...values,
          },
        },
      });

      if (response.data.createAgreement) {
        dispatch(setAgreementDetails(response.data.createAgreement));
        dispatch(handleComplete());
        toast.success("Agreement created successfully!");
      }
    } catch (error) {
      toast.error("Error creating agreement: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAgreement = async (values) => {
    try {
      setIsLoading(true);
      if (imageFile) {
        await uploadData({
          key: values.paymentReceiptFilePath,
          data: imageFile,
        }).result;
      }
      if (agreementFile?.type) {
        await uploadData({
          key: values.agreementFilePath,
          data: agreementFile,
        }).result;
      }

      // Create a new object with only the fields that should be updated
      const updateInput = {
        id: agreementId,
        nameOfSupplier: values.nameOfSupplier,
        dateOfAgreement: values.dateOfAgreement,
        paymentDueDate: values.paymentDueDate,
        currency: values.currency,
        daysOfAgreement: values.daysOfAgreement,
        licensingRegion: values.licensingRegion,
        licensingCountry: values.licensingCountry, // Assuming it should be a string
        carrierType: values.carrierType, // Assuming it should be a string
        numberOfRefreshCycles: values.numberOfRefreshCycles,
        airline: values.airline, // Assuming it should be a string
        amountPaid: parseFloat(values.amountPaid), // Convert to number if needed
        amountRemaining: values.amountRemaining
          ? parseFloat(values.amountRemaining)
          : null,
        numberOfMonthsPerCycle: values.numberOfMonthsPerCycle,
        contractValue: values.contractValue,
        paymentStatus: values.paymentStatus,
        paymentPlan: values.paymentPlan,
        paymentTerm: values.paymentTerm,
        paymentReceiptFilePath: values.paymentReceiptFilePath,
        agreementFilePath: values.agreementFilePath,
      };

      const response = await client.graphql({
        query: updateAgreement,
        variables: {
          input: updateInput,
        },
      });

      if (response.data.updateAgreement) {
        dispatch(setAgreementDetails(response.data.updateAgreement));
        dispatch(handleNext());
        toast.success("Agreement updated successfully!");
      }
    } catch (error) {
      toast.error("Error updating agreement: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const [dollarValue, setDollarValue] = useState("0");

  const getExchangeRates = async () => {
    try {
      const response = await axios.get(EXCHANGE_RATE_URL);
      return response.data.conversion_rates;
    } catch (error) {
      console.error("Error fetching exchange rates:", error);
      return null;
    }
  };

  const calculateDollarValue = async (currency, contractValue) => {
    if (!currency || !contractValue) return "0";

    const rates = await getExchangeRates();
    if (!rates) return "0";

    const rate = rates[currency];
    if (!rate) return "0";

    return (contractValue / rate).toFixed(2);
  };

  useEffect(() => {
    const updateDollarValue = async () => {
      const { currency, contractValue } = formik.values;
      const value = await calculateDollarValue(currency, contractValue);
      setDollarValue(value);
    };

    updateDollarValue();
  }, [formik.values.currency, formik.values.contractValue]);

  useEffect(() => {
    formik.setFieldValue(
      "numberOfMonthsPerCycle",
      formik.values.daysOfAgreement / (formik.values.numberOfRefreshCycles * 30)
    );
  }, [formik.values.numberOfRefreshCycles]);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Box display={"flex"} alignItems={"center"} gap={4}>
          <ReadOnlyInput
            label={"User ID: "}
            value={user?.attributes["custom:name"] || ""}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ minWidth: "300px" }}>
              <Box sx={{ margin: "10px 0" }}>
                <label htmlFor="">
                  Date Of Agreement :{" "}
                  <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                </label>
              </Box>
              <DatePicker
                value={
                  formik.values.dateOfAgreement
                    ? dayjs(formik.values.dateOfAgreement)
                    : null
                }
                sx={{ width: "100%" }}
                name="dateOfAgreement"
                onChange={(newValue) => {
                  const isoDate = newValue ? dayjs(newValue).toISOString() : "";
                  formik.setFieldValue("dateOfAgreement", isoDate); // Save in ISO format
                }}
                renderInput={(params) => (
                  <TextField {...params} margin="normal" fullWidth />
                )}
              />
              {formik.touched.dateOfAgreement &&
                formik.errors.dateOfAgreement && (
                  <Typography sx={{ color: "red" }}>
                    {formik.errors.dateOfAgreement}
                  </Typography>
                )}
            </Box>
          </LocalizationProvider>
        </Box>
        <InputText
          label={"Name of Supplier "}
          placeholder={"supplier"}
          value={formik.values.nameOfSupplier || ""}
          name={"nameOfSupplier"}
          id={"nameOfSupplier"}
          formik={formik}
        />

        <LicensingTerm formik={formik} />
        <Typography my={2} fontSize={20}>
          Region Of License:
        </Typography>
        <Box>
          <Selector
            label={"Target Region"}
            formik={formik}
            options={[{ label: "South Asia", value: "South Asia" }]}
            noneValue={"Select a target region"}
            name={"licensingRegion"}
          />
          <LicensingRegion formik={formik} />
          <TypesOfRights formik={formik} />
        </Box>

        <Box>
          <Typography my={2} fontSize={20}>
            Number of Refresh Cycles
          </Typography>
          <Box display={"flex"} gap={10}>
            <Selector
              noneValue={"Select Number of Referesh Cycles"}
              label={"Number Of Refresh Cycles:"}
              options={[
                { label: "1", value: 1 },
                { label: "2", value: 2 },
                { label: "3", value: 3 },
                { label: "6", value: 6 },
                { label: "9", value: 9 },
              ]}
              formik={formik}
              name="numberOfRefreshCycles"
            />

            <InputText
              type={"number"}
              formik={formik}
              name={"numberOfMonthsPerCycle"}
              label={"Number of months per cycle: "}
              id={"numberOfMonthsPerCycle"}
              value={formik.values.numberOfMonthsPerCycle}
              placeholder={"Enter the number of months per cycle "}
            />
          </Box>
        </Box>

        {/* Commercial Terms */}
        <Typography my={2} fontSize={20}>
          Commercial Terms
        </Typography>
        <Box display={"flex"} gap={4}>
          <Box>
            <Selector
              label={"Currency"}
              name={"currency"}
              options={[
                { label: "USD", value: "USD" },
                { label: "EUR", value: "EUR" },
                { label: "GBP", value: "GBP" },
                { label: "SGD", value: "SGD" },
                { label: "PKR", value: "PKR" },
                { label: "AED", value: "AED" },
                { label: "CNY", value: "CNY" },
                { label: "AUD", value: "AUD" },
              ]}
              formik={formik}
              noneValue={"Select Currency"}
            />
          </Box>
          <Box width={"100%"}>
            <InputText
              placeholder={"Enter the contract value"}
              label={"Contract Value"}
              id={"contractValue"}
              type={"number"}
              name={"contractValue"}
              formik={formik}
              value={formik.values.contractValue}
            />
          </Box>
          <Box width={"100%"}>
            <p>Value in USD Dollars</p>
            {dollarValue}
          </Box>
        </Box>

        <Selector
          noneValue={"Enter Payment Term"}
          label={"Payment Term"}
          options={[
            { label: "Monthly", value: "Monthly" },
            { label: "Subscription", value: "Quarterly" },
            { label: "Bi-Annual", value: "BiAnnual" },
            { label: "Annual", value: "Annual" },
          ]}
          formik={formik}
          name={"paymentTerm"}
        />
        <Selector
          noneValue={"Enter Payment Plan"}
          label={"Payment Plan"}
          options={[
            { label: "Rev-share", value: "RevenueSharing" },
            { label: "Subscription", value: "Subscription" },

            { label: "Payment in advance", value: "PaymentAdvance" },
            { label: "Payment in arrears", value: "PaymentArrears" },
          ]}
          formik={formik}
          name={"paymentPlan"}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ minWidth: "300px" }}>
            <Box sx={{ margin: "10px 0" }}>
              <label htmlFor="">
                Payment Due Date
                <span style={{ color: "red", marginLeft: "5px" }}>*</span>
              </label>
            </Box>
            <DatePicker
              value={
                formik.values.paymentDueDate
                  ? dayjs(formik.values.paymentDueDate)
                  : null
              }
              sx={{ width: "100%" }}
              name="paymentDueDate"
              onChange={(newValue) => {
                const isoDate = newValue ? dayjs(newValue).toISOString() : "";
                formik.setFieldValue("paymentDueDate", isoDate); // Save in ISO format
              }}
              renderInput={(params) => (
                <TextField {...params} margin="normal" fullWidth />
              )}
            />
            {formik.touched.paymentDueDate && formik.errors.paymentDueDate && (
              <Typography sx={{ color: "red" }}>
                {formik.errors.paymentDueDate}
              </Typography>
            )}
          </Box>
        </LocalizationProvider>
        <Selector
          noneValue={"Select the payment status"}
          label={"Payment Status : "}
          options={[
            { label: "Paid", value: "Paid" },
            { label: "In Process", value: "InProcess" },
            { label: "Partially Paid", value: "PartiallyPaid" },
          ]}
          formik={formik}
          name="paymentStatus"
        />
        {(formik.values.paymentStatus === "Paid" ||
          formik.values.paymentStatus === "PartiallyPaid") && (
          <Box my={2}>
            <UploadReceipt formik={formik} setImageFile={setImageFile} />
            <InputText
              type={"number"}
              label={"Amount Paid (USD)"}
              formik={formik}
              placeholder={"Enter the amount paid"}
              name={"amountPaid"}
              id={"amountPaid"}
              value={formik.values.amountPaid}
            />
          </Box>
        )}
        {formik.values.paymentStatus === "PartiallyPaid" && (
          <>
            <InputText
              type={"number"}
              label={"Amount Remaining (PKR)"}
              formik={formik}
              placeholder={"Amount Remaining"}
              name={"amountRemaining"}
              id={"amountRemaining"}
              value={formik.values.amountRemaining}
            />
          </>
        )}

        <AgreementFileUpload
          formik={formik}
          agreementFile={agreementFile}
          setAgreementFile={setAgreementFile}
        />
        {formik?.values?.agreementFilePath && (
          <LoadingButton
            loading={isLoading}
            variant="contained"
            type="submit"
            sx={{
              marginTop: "10px",
              float: "right",
              backgroundColor: "primary.darker",
              color: "secondary.main",
            }}
          >
            Save and Next
          </LoadingButton>
        )}
      </form>
    </div>
  );
};

export default AgreementForm;
